import React, { useCallback } from "react";
import {
  Alert,
  Button,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { useAppDispatch } from "src/redux/hooks";
import { clearSchemaFetchState } from "src/jsonForms/jsonFormsSlice";

interface SchemaFetchErrorProps {
  /**
   * The schema name that failed to fetch. Surfaced in the fallback
   * message when no `errorMessage` is provided so the user can correlate
   * the failure with the LOB and TPA they selected.
   */
  resolvedSchemaName: string;
  /**
   * Failure message surfaced from the rejected `fetchPreScreenSchema`
   * thunk. May be `null` if no error message was captured, in which
   * case a generic fallback is shown.
   */
  errorMessage: string | null;
}

/**
 * Error state rendered by `JsonFormsClaimIntakeApp` when the schema
 * fetch thunk rejects. Surfaces a "Back to form" button that clears the
 * schema-fetch lifecycle so the orchestrator falls back to the pre-screen
 * — this lets the user fix a mis-selected business unit / country / etc.
 * rather than silently retrying the same name. The supporting text
 * embeds an inline support-ticket link for users hitting persistent
 * failures that aren't caused by their input.
 */
function SchemaFetchError({
  resolvedSchemaName,
  errorMessage,
}: SchemaFetchErrorProps): JSX.Element {
  const dispatch = useAppDispatch();

  const handleBackToFormClick = useCallback(() => {
    dispatch(clearSchemaFetchState());
  }, [dispatch]);

  return (
    <Alert
      type="error"
      header="Unable to load form"
      data-testid="schema-fetch-error-alert"
    >
      <SpaceBetween size="m" direction="vertical">
        <span data-testid="schema-fetch-error-message">
          {errorMessage ?? `Schema fetch for "${resolvedSchemaName}" failed.`}
        </span>
        <span data-testid="schema-fetch-error-support-text">
          If the issue persists please{" "}
          <SimLinkGenerator
            simType={SIM_TYPES.REPORT_A_BUG}
            target="_blank"
            external
            ariaLabel="Open a support ticket for this schema fetch failure."
          >
            submit a support ticket
          </SimLinkGenerator>
          .
        </span>
        <Button
          onClick={handleBackToFormClick}
          data-testid="schema-fetch-back-to-form-button"
        >
          Back to form
        </Button>
      </SpaceBetween>
    </Alert>
  );
}

export default SchemaFetchError;
